// app/frontend/components/ui/Admin/AdminInfoCard.tsx
import React from 'react';
import { Card, Grid, Text, Group, Avatar, Badge, ActionIcon, Box } from '@mantine/core';
import { IconMail, IconShield, IconCalendar, IconUser, IconHash } from '@tabler/icons-react';
import { formatDistanceToNow } from 'date-fns';

import { LightCappedBadge } from '../../../components/ui';

interface AdminInfoPanelProps {
  entity: {
    id: number;
    created_at?: string;
    updated_at?: string;
    user?: {
      id: number;
      username: string;
      email: string;
      avatar_url?: string | null;
    } | null;
    [key: string]: any;
  };
  entityType?: string;
}

const AdminInfoPanel: React.FC<AdminInfoPanelProps> = ({ entity, entityType = 'Event' }) => {
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return 'Invalid date';
    }
  };

  const handleEmailClick = (email: string) => {
    window.location.href = `mailto:${email}`;
  };

  return (
    <Card
      radius="xs"
      px={0}
      style={{
        backgroundColor: '#17171710',
        transition: 'all 0.2s ease',
        fontFamily: 'Poppins, sans-serif',
      }}
    >
      {/* SuperAdmin Badge - Corner indicator */}
      <LightCappedBadge
        color="orange"
        size="md"
        style={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: '16px',
          fontWeight: 600,
        }}
        icon={<IconShield size={16} />}
      >
        Super Admin
      </LightCappedBadge>

      <Grid gutter="8px" mt="8px" align="stretch">
        {/* Event ID Section */}
        <Grid.Col span={{ base: 12, sm: 4 }} my="0">
          <Box
            p="xs"
            style={{
              backgroundColor: 'rgba(164, 164, 164, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(111, 111, 111, 0.2)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
            }}
          >
            <Group gap="4px" mb="xs">
              <IconHash size={16} color="#f2f2f2" />
              <Text size="sm" fw={700} c="#f2f2f2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                {entityType} ID
              </Text>
            </Group>
            <Text size="md" fw={500} c="white" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {entity.id}
            </Text>
          </Box>
        </Grid.Col>

        {/* Created By Section */}
        <Grid.Col span={{ base: 12, sm: 4 }} my="0px">
          <Box
            p="md"
            style={{
              backgroundColor: 'rgba(164, 164, 164, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(111, 111, 111, 0.2)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
            }}
          >
            <Group gap="4px" mb="md">
              <IconUser size={16} color="#9CA3AF" />
              <Text size="sm" fw={700} c="#f2f2f2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Created by
              </Text>
            </Group>

            {entity.user ? (
              <Group gap="sm">
                <Avatar
                  src={entity.user.avatar_url}
                  size="sm"
                  radius="xl"
                  style={{
                    border: '2px solid rgba(255, 255, 255, 0.1)',
                  }}
                />
                <Box flex={1}>
                  <Text size="sm" fw={500} c="white" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    {entity.user.username}
                  </Text>
                  <Group gap="xs" mt={2}>
                    <Text
                      size="xs"
                      c="#9CA3AF"
                      style={{
                        fontFamily: 'Poppins, sans-serif',
                        cursor: 'pointer',
                        transition: 'color 0.2s ease',
                      }}
                      onClick={() => handleEmailClick(entity.user!.email)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#EA580C';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#9CA3AF';
                      }}
                    >
                      {entity.user.email}
                    </Text>
                    <ActionIcon
                      variant="subtle"
                      size="xs"
                      color="gray"
                      onClick={() => handleEmailClick(entity.user!.email)}
                      style={{
                        transition: 'all 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#EA580C';
                        e.currentTarget.style.transform = 'scale(1.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#9CA3AF';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <IconMail size={12} />
                    </ActionIcon>
                  </Group>
                </Box>
              </Group>
            ) : (
              <Text size="sm" c="#6B7280" style={{ fontFamily: 'Poppins, sans-serif' }}>
                No user assigned
              </Text>
            )}
          </Box>
        </Grid.Col>

        {/* Timeline Section */}
        <Grid.Col span={{ base: 12, sm: 4 }} my="0">
          <Box
            p="md"
            style={{
              backgroundColor: 'rgba(164, 164, 164, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(111, 111, 111, 0.2)',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
            }}
          >
            <Group gap="4px" mb="md">
              <IconCalendar size={16} color="#9CA3AF" />
              <Text size="sm" fw={700} c="#f2f2f2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Timeline
              </Text>
            </Group>

            <Box>
              <Group justify="space-between" mb="xs">
                <Text size="xs" c="#6B7280" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Created
                </Text>
                <Text size="xs" c="white" fw={500} style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {formatDate(entity.created_at)}
                </Text>
              </Group>

              <Group justify="space-between">
                <Text size="xs" c="#6B7280" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Updated
                </Text>
                <Text size="xs" c="white" fw={500} style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {formatDate(entity.updated_at)}
                </Text>
              </Group>
            </Box>
          </Box>
        </Grid.Col>
      </Grid>
    </Card>
  );
};

export default AdminInfoPanel;
